import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { Button } from "./ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import LoadingButton from "./LoadingButton";
// import { Checkbox } from "./ui/checkbox";
import { useAuth } from "../context/AuthContext";
import { Toast, ToastDescription, ToastTitle, ToastViewport } from "./ui/toast";
import { useUserType } from "../context/UserTypeContext";

const API_URL = process.env.REACT_APP_API_URL; // Ensure this matches your backend URL

const SignIn = () => {
  const username = process.env.REACT_APP_API_USERNAME;
  const password = process.env.REACT_APP_API_PASSWORD;
  const credentials = `${username}:${password}`;
  const encodedCredentials = btoa(credentials); // Base64 encode the credentials

  const { login } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ userName: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // const [isCheckboxEnabled, setIsCheckboxEnabled] = useState(false);
  // const [isCheckboxChecked, setIsCheckboxChecked] = useState(false);
  // const [termsAndConditionsViewed, setTermsAndConditionsViewed] =
  //   useState(false);
  // eslint-disable-next-line no-unused-vars
  const [userSession, setUserSession] = useState(null); // State to hold user session data

  // const [termsColor, setTermsColor] = useState("#00B5ED");
  const [toastList, setToastList] = useState([]);

  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  const { userType } = useUserType();

  // useEffect(() => {
  //   // Enabling checkbox when both documents are viewed
  //   if (termsAndConditionsViewed) {
  //     setIsCheckboxEnabled(true);
  //     setIsCheckboxChecked(true);
  //   }
  // }, [termsAndConditionsViewed]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.id]: e.target.value });
  };
  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/user/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${encodedCredentials}`, // Basic Auth header
          "user-type": userType,
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        const responseData = await response.json(); // Parse JSON response

        // Check if login is successful
        if (responseData.data?.success) {
          const loggedInUser = responseData.data.loggedInUser;
          login(loggedInUser); // Update session context
          const apiToken = loggedInUser.apiToken; // Extract the apiToken

          // Store the token and user session
          localStorage.setItem("x-auth-token", apiToken); // Store the token
          localStorage.setItem("userSession", JSON.stringify(loggedInUser)); // Store the user session

          setUserSession(loggedInUser); // Update session state

          setToastList((prev) => [
            ...prev,
            {
              id: Date.now(),
              title: "SUCCESS!",
              description: responseData.data?.message || "Sign in successful!",
              variant: "default",
            },
          ]);

          navigate("/nextsteps");
        } else {
          setToastList((prev) => [
            ...prev,
            {
              id: Date.now(),
              title: "ERROR!",
              description:
                responseData.data?.message ||
                "Invalid email and password. Sign up.",
              variant: "destructive",
            },
          ]);
          setIsLoading(false);
        }
      } else {
        const responseData = await response.json(); // Parse JSON response
        setToastList((prev) => [
          ...prev,
          {
            id: Date.now(),
            title: "ERROR!",
            description:
              responseData.data?.message ||
              "Invalid username and password. Sign up.",
            variant: "destructive",
          },
        ]);
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error:", error);
      setToastList((prev) => [
        ...prev,
        {
          id: Date.now(),
          title: "ERROR!",
          description: "An error occurred.",
          variant: "destructive",
        },
      ]);
      setIsLoading(false);
    }
  };

  // const handleTermsAndConditionsClick = (url) => {
  //   window.open(url, "_blank");
  //   setTermsAndConditionsViewed(true);
  //   setTermsColor("#22c55e");
  // };

  const removeToast = (id) => {
    setToastList((prev) => prev.filter((toast) => toast.id !== id));
  };

  return (
    <div className="tw-flex tw-flex-col tw-min-h-screen">
      {/* Left side (visible as blue only on desktop) */}
      {/* <div className="tw-absolute -tw-inset-24 tw-z-0 md:tw-w-[54%] tw-hidden tw-min-h-full md:tw-flex md:tw-flex-1 tw-bg-[#f1fcff]"></div>
      <div className="tw-hidden md:tw-flex md:tw-w-[50%] tw-relative tw-min-h-screen">
        <div className="tw-flex md:tw-w-[70%] tw-justify-center tw-items-center tw-h-full tw-absolute tw-inset-0 tw-z-0">
          <img
            src="/authimg.png"
            alt="Sign In Illustration"
            className="md:tw-h-[40%]"
          />
        </div>
      </div> */}

      {/* Right side (white background visible on both desktop and mobile) */}
      <div className="tw-flex tw-flex-col tw-h-screen tw-items-center tw-w-full tw-justify-center">
        <div className="md:tw-w-[70%]">
          <Card className="tw-w-full tw-shadow-none tw-border-none tw-mb-0 tw-bg-transparent">
            <CardHeader className="tw-px-0">
              <CardTitle className="tw-text-xl md:tw-text-3xl h-sm:tw-text-2xl">
                Sign In
              </CardTitle>
              <CardDescription className="tw-text-[#828282]">
                {`Enter your ${
                  userType === "applicator" ? "applicator id" : "email"
                } and password`}
              </CardDescription>
            </CardHeader>
            <CardContent className="tw-px-0">
              <form onSubmit={handleSubmit}>
                <div className="tw-grid tw-grid-cols-1 tw-gap-6 md:tw-gap-8">
                  <div className="tw-grid tw-gap-2">
                    <Label className="tw-font-medium" htmlFor="userName">
                      {`${
                        userType === "applicator" ? "Applicator ID" : "Email"
                      }`}
                    </Label>
                    <Input
                      className="placeholder:tw-text-[#828282]/50 placeholder:tw-text-xs placeholder:md:tw-text-sm"
                      id="userName"
                      type="userName"
                      value={formData.userName}
                      onChange={handleChange}
                      placeholder={`${
                        userType === "applicator"
                          ? "Enter applicator id"
                          : "Enter email address"
                      }`}
                      required
                    />
                  </div>
                  <div className="tw-grid tw-gap-2">
                    <Label className="tw-font-medium" htmlFor="password">
                      Password
                    </Label>
                    <div className="tw-relative tw-flex tw-items-center">
                      <Input
                        className="placeholder:tw-text-[#828282]/50 placeholder:tw-text-xs placeholder:md:tw-text-sm tw-pr-10"
                        id="password"
                        type={showPassword ? "text" : "password"}
                        value={formData.password}
                        onChange={handleChange}
                        placeholder="Enter password"
                        required
                      />
                      <button
                        type="button"
                        onClick={togglePasswordVisibility}
                        className="tw-absolute tw-right-3 tw-flex tw-items-center tw-text-[#828282] tw-h-5 tw-w-5 hover:tw-bg-transparent"
                        aria-label="Toggle password visibility"
                        tabIndex="-1"
                      >
                        {showPassword ? <EyeOff /> : <Eye />}
                      </button>
                    </div>
                    <p className="tw-text-xs tw-italic tw-opacity-60">
                      Password must be at least 8 characters and include an
                      uppercase letter (A–Z), lowercase letter (a–z), a number
                      (0–9), and a special character (e.g. !@#$%).
                    </p>
                  </div>
                </div>

                {/* <p className="tw-text-center tw-text-[10px] tw-mt-3 tw-italic tw-text-gray-500">
                  Click on the Site Use Terms to get the checkbox ticked
                  automatically
                </p>

                <div className="tw-flex tw-items-center tw-space-x-2 tw-justify-center tw-mt-1">
                  <Checkbox
                    id="termsCheckbox"
                    disabled={!isCheckboxEnabled}
                    checked={isCheckboxChecked}
                    onChange={(e) => setIsCheckboxChecked(e.target.checked)}
                    required
                  />
                  <span className="tw-text-xs md:tw-text-sm">
                    I agree to the terms of the{" "}
                    <span
                      type="text"
                      onClick={() =>
                        handleTermsAndConditionsClick("/terms.pdf")
                      }
                      className="tw-cursor-pointer tw-font-medium hover:tw-underline"
                      style={{ color: termsColor }}
                    >
                      Site Use Terms and Conditions
                    </span>
                  </span>
                </div> */}

                {isLoading ? (
                  <LoadingButton />
                ) : (
                  <Button
                    // disabled={!isCheckboxEnabled}
                    type="submit"
                    className="tw-w-full tw-font-semibold tw-mt-6 md:tw-mt-8 tw-text-sm"
                  >
                    SIGN IN
                  </Button>
                )}
              </form>
            </CardContent>
          </Card>
          <div className="tw-text-center tw-text-xs md:tw-text-sm">
            Don&apos;t have an account?{" "}
            <Link to={userType === "applicator" ? "/register" : "/signup"}>
              <span className="tw-text-[#00B5ED] tw-font-medium">Sign up</span>
            </Link>
          </div>
          <Link
            to="/forgotpassword"
            className="tw-text-xs md:tw-text-sm hover:tw-underline"
          >
            <p className="tw-text-center tw-mt-1 tw-text-[#00B5ED]">
              Forgot password?
            </p>
          </Link>
        </div>
      </div>

      {/* Toast Notifications */}
      <ToastViewport />
      {toastList.map(({ id, title, description, variant }) => (
        <Toast
          key={id}
          variant={variant}
          open={true}
          duration={Infinity}
          onOpenChange={(isOpen) => {
            if (!isOpen) removeToast(id);
          }}
          className="tw-flex tw-flex-col tw-text-center"
        >
          <ToastTitle>{title}</ToastTitle>
          <ToastDescription>{description}</ToastDescription>

          {/* Close Button */}
          <button
            onClick={() => removeToast(id)}
            className="tw-mt-3 tw-self-center tw-text-sm tw-font-medium tw-bg-white tw-px-2 tw-py-1 tw-text-black hover:tw-bg-white/80"
          >
            Close
          </button>
        </Toast>
      ))}
    </div>
  );
};

export default SignIn;
